import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProtectedRoute from '../components/ProtectedRoute';
import { Lock, AlertTriangle, Check } from 'lucide-react';

const FWB_SAMPLE = [
  { name: 'Sample (25)', tags: ['FWB'], blurred: true },
  { name: 'Sample (27)', tags: ['FWB'], blurred: true },
  { name: 'Verified (26)', tags: ['FWB'], verified: true, blurred: false },
  { name: 'Sample (28)', tags: ['FWB'], blurred: true },
];

const FWBPage = () => {
  const { userProfile } = useAuth();
  const [consent, setConsent] = useState(false);
  const [entered, setEntered] = useState(false);

  const isPremium = userProfile?.subscriptionStatus === 'premium';

  if (!entered) {
    return (
      <ProtectedRoute>
        <div className="max-w-xl mx-auto px-4 py-16">
          <div className="rounded-2xl border-2 border-amber-500/50 bg-slate-800/80 p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-8 w-8 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <h2 className="font-heading font-bold text-lg text-white mb-2">18+ Only</h2>
                <p className="text-slate-400 text-sm mb-4">
                  This section is for adults only. Friendship with Benefits (FWB) involves consensual connections
                  between adults. By entering, you confirm you are 18 or older and agree to use this space
                  responsibly and respectfully.
                </p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="rounded text-brand-pink"
                  />
                  <span className="text-slate-300 text-sm font-medium">
                    I am 18 or older and agree to the above.
                  </span>
                </label>
              </div>
            </div>
          </div>
          <button
            type="button"
            disabled={!consent}
            onClick={() => setEntered(true)}
            className="w-full py-3 rounded-xl bg-brand-pink text-white font-semibold hover:bg-brand-pink-dark disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Enter FWB Zone
          </button>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <div className="max-w-6xl mx-auto px-4 py-12">
        <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-2">
          Friendship with Benefits
        </h1>
        <p className="text-slate-400 mb-8">
          Connect with verified adults for consensual FWB connections. Full profiles and chat require premium.
        </p>
        {!isPremium && (
          <div className="p-4 rounded-2xl bg-slate-800/80 border border-amber-500/40 flex items-center justify-between flex-wrap gap-4 mb-8">
            <p className="text-amber-200 font-medium">Unlock FWB profiles and messaging with premium.</p>
            <Link
              to="/payment"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-pink text-white font-semibold hover:bg-brand-pink-dark"
            >
              <Lock className="h-4 w-4" />
              Upgrade
            </Link>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FWB_SAMPLE.map((p, i) => {
            const shouldBlur = p.blurred && !isPremium;
            return (
              <div
                key={i}
                className={`rounded-2xl overflow-hidden bg-slate-800/80 border border-slate-700 ${
                  p.verified ? 'ring-2 ring-brand-pink/40' : ''
                }`}
              >
                <div className="aspect-[3/4] relative bg-slate-800">
                  {shouldBlur ? (
                    <>
                      <div className="absolute inset-0 backdrop-blur-xl bg-slate-900/60" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Lock className="h-10 w-10 text-brand-pink" />
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-700">
                      <span className="text-slate-500 text-sm">{p.verified ? 'Verified Friend' : 'Profile'}</span>
                    </div>
                  )}
                  {p.verified && (
                    <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-brand-pink flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-white">{p.name}</h3>
                  <p className="text-xs text-slate-400 mt-1">{p.tags.join(' | ')}</p>
                </div>
              </div>
            );
          })}
        </div>
        {!isPremium && (
          <p className="mt-6 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
            <Lock className="h-4 w-4 text-amber-400/90" />
            Premium required for full profiles and messaging
          </p>
        )}
      </div>
    </ProtectedRoute>
  );
};

export default FWBPage;
